import React from 'react';
import PropTypes from 'prop-types';
import ContentCard from '../ContentCard';
import {
  FoodPlatterIcon,
  ApplicableDaysIcon,
  BudgetPerEmployeeIcon,
  UnionIcon,
} from '../SVGs/commonSVGs';
import {
  orderServiceTypeEnum,
  dayNamesEnum,
  cycleTypeSuffixEnum,
  statusLabels,
} from '../../pages/common/components/constants';
import { formatDays } from '../../helpers';
import {
  InfoGrid,
  InfoRow,
  InfoLabel,
  InfoValue,
} from '../common/styles';
import ListInfoModal from './ListInfoModal';
import SlotItem from './SlotItem';

const getQuestionLabel = question => {
  let label = question?.title || '';

  if (question?.applicable_business_types?.length > 0) {
    const businessTypes = question.applicable_business_types
      .map(bt => statusLabels[orderServiceTypeEnum[bt]])
      .filter(Boolean);
    if (businessTypes.length > 0) {
      label += ` (${businessTypes.join(', ')})`;
    }
  }

  return label;
};

const getApplicableServices = (businessTypes = []) => {
  if (!businessTypes.length) return 'NA';

  const services = businessTypes
    .map(type => statusLabels[orderServiceTypeEnum[type]])
    .filter(Boolean);

  return services.join(' & ') || 'NA';
};

const getApplicableDays = (dayRes = []) => {
  if (!dayRes.length) return 'NA';

  const days = dayRes.map(d => dayNamesEnum[d.day]).filter(Boolean);
  return formatDays(days);
};

const extractSlots = (dayRes = []) => {
  const slotsMap = new Map();

  dayRes.forEach(dayData => {
    if (!dayData.slot_res?.length) return;

    const dayName = dayNamesEnum[dayData.day];

    dayData.slot_res.forEach(slot => {
      // Build POI identifier string (sorted for consistency)
      const poiKey = (slot.poi_res || [])
        .map(poi => `${poi.id}:${poi.apl_rule}`)
        .sort()
        .join('|');

      const perOrderLimit = slot.order_res?.per_order_limit_value || '';
      const slotKey = `${slot.amount}-${slot.start_time}-${slot.end_time}-${poiKey}-${perOrderLimit}`;

      if (!slotsMap.has(slotKey)) {
        slotsMap.set(slotKey, {
          id: slot.id,
          start_time: slot.start_time,
          end_time: slot.end_time,
          amount: slot.amount,
          per_order_amount: slot.order_res?.per_order_limit_value,
          locations: slot.poi_res || [],
          days: new Set(),
        });
      }

      if (dayName) {
        slotsMap.get(slotKey).days.add(dayName);
      }
    });
  });

  return Array.from(slotsMap.values()).map(slot => ({
    ...slot,
    days: Array.from(slot.days),
  }));
};

const PolicyCard = ({ policy }) => {
  const applicableServices = getApplicableServices(policy?.applicable_business_types);
  const applicableDays = getApplicableDays(policy?.restriction_details?.day_res);
  const budgetAmount = policy?.restriction_details?.amount || 0;
  const cycleType = policy?.restriction_details?.cycle_type;
  const frequencySuffix = cycleTypeSuffixEnum[cycleType] || 'per month';
  const policyQuestions = policy?.child_question_details || [];
  const slots = extractSlots(policy?.restriction_details?.day_res);

  return (
    <ContentCard bordered>
      <ContentCard.Section>
        <ContentCard.Subtitle>
          {policy?.title || 'Untitled Policy'}
        </ContentCard.Subtitle>

        <ContentCard.Divider />

        <InfoGrid>
          <InfoRow>
            <InfoLabel>
              <FoodPlatterIcon />
              Applicable Services
            </InfoLabel>
            <InfoValue>{applicableServices}</InfoValue>
          </InfoRow>

          <InfoRow>
            <InfoLabel>
              <ApplicableDaysIcon />
              Applicable Days
            </InfoLabel>
            <InfoValue>{applicableDays}</InfoValue>
          </InfoRow>

          <InfoRow>
            <InfoLabel>
              <BudgetPerEmployeeIcon />
              Budget per Employee
            </InfoLabel>
            <InfoValue>
              ₹
              {budgetAmount}
              {' '}
              {frequencySuffix}
            </InfoValue>
          </InfoRow>

          <InfoRow>
            <InfoLabel>
              <UnionIcon />
              Employee Questions
            </InfoLabel>
            <InfoValue>
              {policyQuestions.length > 0 ? (
                <ListInfoModal
                  items={policyQuestions}
                  itemLabel="question"
                  modalTitle="Questions added"
                  getItemText={getQuestionLabel}
                  renderIcon={() => <UnionIcon />}
                />
              ) : 'NA'}
            </InfoValue>
          </InfoRow>
        </InfoGrid>

        {slots.length > 0 && (
          <ContentCard.Section>
            {
              slots.map((slot, slotIndex) => (
                <SlotItem key={slot.id} slot={slot} slotIndex={slotIndex} />
              ))
            }
          </ContentCard.Section>
        )}
      </ContentCard.Section>
    </ContentCard>
  );
};

PolicyCard.propTypes = {
  policy: PropTypes.objectOf(PropTypes.any),
};

PolicyCard.defaultProps = {
  policy: {},
};

export default PolicyCard;
